type PromptVisibilityConfig = {
  showSuggestedPrompts: boolean;
  showContextualTeaser: boolean;
  teaserDelayMs: number;
};

const DEFAULT_PROMPT_VISIBILITY: PromptVisibilityConfig = {
  showSuggestedPrompts: false,
  showContextualTeaser: false,
  teaserDelayMs: 2000,
};

export function extractPromptVisibilityConfig(settings: unknown): PromptVisibilityConfig {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return { ...DEFAULT_PROMPT_VISIBILITY };
  }

  const root = settings as Record<string, unknown>;
  const widgetRaw =
    root.widget && typeof root.widget === "object" && !Array.isArray(root.widget)
      ? (root.widget as Record<string, unknown>)
      : null;
  const promptsRaw =
    widgetRaw?.prompts && typeof widgetRaw.prompts === "object" && !Array.isArray(widgetRaw.prompts)
      ? (widgetRaw.prompts as Record<string, unknown>)
      : null;

  if (!promptsRaw) return { ...DEFAULT_PROMPT_VISIBILITY };

  const showSuggestedPrompts =
    typeof promptsRaw.showSuggestedPrompts === "boolean"
      ? promptsRaw.showSuggestedPrompts
      : DEFAULT_PROMPT_VISIBILITY.showSuggestedPrompts;
  const showContextualTeaser =
    typeof promptsRaw.showContextualTeaser === "boolean"
      ? promptsRaw.showContextualTeaser
      : DEFAULT_PROMPT_VISIBILITY.showContextualTeaser;

  const rawDelay =
    typeof promptsRaw.teaserDelayMs === "number"
      ? promptsRaw.teaserDelayMs
      : DEFAULT_PROMPT_VISIBILITY.teaserDelayMs;
  const teaserDelayMs = clampDelay(rawDelay);

  return {
    showSuggestedPrompts,
    showContextualTeaser,
    teaserDelayMs,
  };
}

function clampDelay(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_PROMPT_VISIBILITY.teaserDelayMs;
  return Math.max(0, Math.min(10000, Math.round(value)));
}

export type OpeningQuestionMode = "predefined" | "contextual";
export type OpeningQuestionLocale = "en" | "es";
export type OpeningQuestionSettings = {
  openingMode: OpeningQuestionMode;
  openingLimit: 1 | 2 | 3;
  openingQuestionsByLocale: Partial<Record<OpeningQuestionLocale, string[]>>;
  questionEntriesByLocale?: Partial<Record<OpeningQuestionLocale, OpeningQuestion[]>>;
  contextualQuestionGroups?: ContextualQuestionGroup[];
};
export type OpeningQuestion = { id: string; question: string; label?: string };
export type ContextualQuestionGroup = {
  id: string;
  path: string;
  additionalPaths?: string[];
  questionsByLocale: Partial<Record<OpeningQuestionLocale, OpeningQuestion[]>>;
};
export const OPENING_QUESTION_MAX_LENGTH = 240;
export const CONTEXTUAL_GROUP_MAX_PATHS = 32;

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

export function legacyQuestionId(question: string): string {
  let hash = 2166136261;
  for (const char of question) hash = Math.imul(hash ^ char.codePointAt(0)!, 16777619);
  return `legacy-${(hash >>> 0).toString(36)}`;
}

export function createConfiguredQuestionId(): string {
  return `q-${typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`;
}

function entries(value: unknown): OpeningQuestion[] {
  if (!Array.isArray(value) || value.length > 3) throw new Error("Invalid question entries");
  const ids = new Set<string>();
  return value.flatMap((raw) => {
    const item = record(raw);
    if (typeof item.id !== "string" || !/^[a-zA-Z0-9_-]{1,80}$/.test(item.id) || ids.has(item.id)) throw new Error("Invalid question identifier");
    ids.add(item.id);
    const question = questionList([item.question])[0];
    if (!question) return [];
    const label = item.label === undefined || item.label === "" ? undefined : questionList([item.label])[0];
    if (label && Array.from(label).length > 80) throw new Error("Question label is too long");
    return [{ id: item.id, question, ...(label ? { label } : {}) }];
  });
}

function localizedEntries(value: unknown, enabledLocales: readonly string[]) {
  const result: NonNullable<OpeningQuestionSettings["questionEntriesByLocale"]> = {};
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid question languages");
  for (const [locale, list] of Object.entries(value)) {
    if ((locale !== "en" && locale !== "es") || !enabledLocales.includes(locale)) throw new Error("Question language is not enabled");
    result[locale] = entries(list);
  }
  return result;
}

function pagePath(value: unknown, trim = false): string | null {
  if (typeof value !== "string") return null;
  const candidate = trim ? value.trim() : value;
  if (!/^\/(?!\/)[^?#<>\s]*$/.test(candidate) || candidate.length > 240) return null;
  return candidate.replace(/\/+$/, "") || "/";
}

function groups(value: unknown, enabledLocales: readonly string[]): ContextualQuestionGroup[] {
  if (!Array.isArray(value) || value.length > 10) throw new Error("At most ten contextual question groups");
  const ids = new Set<string>();
  const paths = new Set<string>();
  return value.map((raw) => {
    const group = record(raw);
    if (typeof group.id !== "string" || !/^[a-zA-Z0-9_-]{1,80}$/.test(group.id) || ids.has(group.id)) throw new Error("Invalid group identifier");
    ids.add(group.id);
    const path = pagePath(group.path);
    if (!path) throw new Error("Use an exact page path without query or fragment");
    if (group.additionalPaths !== undefined && (!Array.isArray(group.additionalPaths) || group.additionalPaths.length > CONTEXTUAL_GROUP_MAX_PATHS * 2)) throw new Error("Invalid additional page paths");
    const additionalPaths = ((group.additionalPaths ?? []) as unknown[]).flatMap(value => {
      if (typeof value !== "string") throw new Error("Use an exact page path without query or fragment");
      const candidate = value.trim();
      if (!candidate) return [];
      const normalized = pagePath(candidate);
      if (!normalized) throw new Error("Use an exact page path without query or fragment");
      return [normalized];
    });
    if (additionalPaths.length >= CONTEXTUAL_GROUP_MAX_PATHS) throw new Error(`At most ${CONTEXTUAL_GROUP_MAX_PATHS} page paths per question group`);
    for (const candidate of [path, ...additionalPaths]) {
      if (paths.has(candidate)) throw new Error("One question group per page path");
      paths.add(candidate);
    }
    return { id: group.id, path, ...(additionalPaths.length ? { additionalPaths } : {}), questionsByLocale: localizedEntries(group.questionsByLocale, enabledLocales) };
  });
}

// Reject markup and invisible controls; text is displayed and sent as an ordinary
// user question. It is never interpolated into assistant instructions.
function questionList(value: unknown): string[] {
  if (!Array.isArray(value) || value.length > 3) throw new Error("Invalid opening questions");
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") throw new Error("Invalid opening question");
    const text = item.trim();
    if (Array.from(text).length > OPENING_QUESTION_MAX_LENGTH || /[<>\p{Cc}\p{Cf}]/u.test(text)) {
      throw new Error("Opening questions must be plain text of at most 240 characters");
    }
    const key = text.toLowerCase();
    if (text && !seen.has(key)) { seen.add(key); result.push(text); }
  }
  return result;
}

/** Validate only supplied keys so unrelated/partial settings saves remain merges. */
export function validateOpeningQuestionPatch(value: unknown, enabledLocales: readonly string[]): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid widget prompt settings");
  }
  const patch = { ...value as Record<string, unknown> };
  if ("questionEntriesByLocale" in patch) {
    const normalized = localizedEntries(patch.questionEntriesByLocale, enabledLocales);
    patch.questionEntriesByLocale = normalized;
    patch.openingQuestionsByLocale = { ...record(patch.openingQuestionsByLocale), ...Object.fromEntries(Object.entries(normalized).map(([locale, list]) => [locale, list.map((item) => item.question)])) };
  }
  if ("contextualQuestionGroups" in patch) patch.contextualQuestionGroups = groups(patch.contextualQuestionGroups, enabledLocales);
  if ("openingMode" in patch && patch.openingMode !== "predefined" && patch.openingMode !== "contextual") {
    throw new Error("Invalid opening question mode");
  }
  if ("openingLimit" in patch && ![1, 2, 3].includes(patch.openingLimit as number)) {
    throw new Error("Opening question maximum must be 1, 2 or 3");
  }
  if ("openingQuestionsByLocale" in patch) {
    const raw = patch.openingQuestionsByLocale;
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) throw new Error("Invalid question languages");
    const normalized: Partial<Record<OpeningQuestionLocale, string[]>> = {};
    for (const [locale, list] of Object.entries(raw)) {
      if ((locale !== "en" && locale !== "es") || !enabledLocales.includes(locale)) {
        throw new Error("Opening questions require an enabled supported language");
      }
      normalized[locale] = questionList(list);
    }
    patch.openingQuestionsByLocale = normalized;
  }
  for (const key of ["showSuggestedPrompts", "showContextualTeaser"]) {
    if (key in patch && typeof patch[key] !== "boolean") throw new Error("Invalid prompt visibility");
  }
  return patch;
}

export function extractOpeningQuestionSettings(settings: unknown): OpeningQuestionSettings {
  const prompts = record(record(record(settings).widget).prompts);
  const questions: OpeningQuestionSettings["openingQuestionsByLocale"] = {};
  let questionEntriesByLocale: OpeningQuestionSettings["questionEntriesByLocale"] = {};
  let contextualQuestionGroups: ContextualQuestionGroup[] = [];
  try { if (prompts.questionEntriesByLocale) questionEntriesByLocale = localizedEntries(prompts.questionEntriesByLocale, ["en", "es"]); } catch { /* Invalid stored configuration fails closed. */ }
  try { if (prompts.contextualQuestionGroups) contextualQuestionGroups = groups(prompts.contextualQuestionGroups, ["en", "es"]); } catch { /* Invalid groups are not exposed. */ }
  for (const locale of ["en", "es"] as const) {
    const list = record(prompts.openingQuestionsByLocale)[locale];
    if (list === undefined) continue;
    try { questions[locale] = questionList(list); } catch { /* Invalid stored text fails closed. */ }
  }
  return {
    openingMode: prompts.openingMode === "predefined" ? "predefined" : "contextual",
    openingLimit: prompts.openingLimit === 1 || prompts.openingLimit === 2 ? prompts.openingLimit : 3,
    openingQuestionsByLocale: questions,
    questionEntriesByLocale,
    contextualQuestionGroups,
  };
}

export function resolveOpeningQuestions(opening: OpeningQuestionSettings, locale: string | undefined | null, url?: string): OpeningQuestion[] {
  if (locale !== "en" && locale !== "es") return [];
  if (opening.openingMode === "contextual" && url) {
    try {
      const path = pagePath(new URL(url).pathname);
      const group = path ? opening.contextualQuestionGroups?.find((item) => pagePath(item.path) === path || item.additionalPaths?.some(candidate => pagePath(candidate, true) === path)) : undefined;
      const contextual = group?.questionsByLocale[locale];
      if (contextual?.length) return contextual.slice(0, opening.openingLimit);
    } catch { /* Use only configured fallback. */ }
  }
  const configured = opening.questionEntriesByLocale?.[locale];
  const legacy = opening.openingQuestionsByLocale[locale];
  const fallback = legacy ? legacy.map((question) => configured?.find((item) => item.question === question) ?? { id: legacyQuestionId(question), question }) : configured ?? [];
  return fallback.slice(0, opening.openingLimit);
}

export function filterOpeningQuestionLanguages(opening: OpeningQuestionSettings, enabledLocales: readonly string[]): OpeningQuestionSettings {
  const enabled = (value: Record<string, unknown>) => Object.fromEntries(Object.entries(value).filter(([locale]) => enabledLocales.includes(locale)));
  return { ...opening, openingQuestionsByLocale: enabled(opening.openingQuestionsByLocale), questionEntriesByLocale: enabled(opening.questionEntriesByLocale ?? {}), contextualQuestionGroups: (opening.contextualQuestionGroups ?? []).map((group) => ({ ...group, questionsByLocale: enabled(group.questionsByLocale) })) };
}

/** Bootstrap selects the saved locale fallback. The widget resolves configured
 * page groups locally; no knowledge load or question-generation call is added. */
export function selectOpeningQuestions(input: {
  settings: unknown;
  effectivePlan: string;
  locale: string | null | undefined;
  enabledLocales: readonly string[];
}): string[] {
  if (input.effectivePlan !== "pro" && input.effectivePlan !== "custom") return [];
  if (!extractPromptVisibilityConfig(input.settings).showSuggestedPrompts) return [];
  if ((input.locale !== "en" && input.locale !== "es") || !input.enabledLocales.includes(input.locale)) return [];
  const opening = extractOpeningQuestionSettings(input.settings);
  return resolveOpeningQuestions(opening, input.locale).map((item) => item.question);
}

export function buildOpeningQuestionPayload(input: Parameters<typeof selectOpeningQuestions>[0]) {
  const visibility = extractPromptVisibilityConfig(input.settings);
  const eligible = input.effectivePlan === "pro" || input.effectivePlan === "custom";
  const opening = extractOpeningQuestionSettings(input.settings);
  // Do not expose disabled-language lists through the public session payload.
  opening.openingQuestionsByLocale = Object.fromEntries(
    Object.entries(opening.openingQuestionsByLocale).filter(([locale]) => eligible && input.enabledLocales.includes(locale)),
  );
  opening.questionEntriesByLocale = Object.fromEntries(Object.entries(opening.questionEntriesByLocale ?? {}).filter(([locale]) => eligible && input.enabledLocales.includes(locale)));
  opening.contextualQuestionGroups = (opening.contextualQuestionGroups ?? []).map((group) => ({ ...group, questionsByLocale: Object.fromEntries(Object.entries(group.questionsByLocale).filter(([locale]) => eligible && input.enabledLocales.includes(locale))) })).filter((group) => Object.keys(group.questionsByLocale).length > 0);
  return {
    prompt_visibility: {
      ...visibility,
      ...opening,
      showSuggestedPrompts: eligible && visibility.showSuggestedPrompts,
      showContextualTeaser: ["growth", "pro", "custom"].includes(input.effectivePlan) && visibility.showContextualTeaser,
    },
    prompts: { teaser: null, prompts: selectOpeningQuestions(input) },
  };
}
